import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, Users, X, Check } from "lucide-react";
import PersonAvatar from "../people/PersonAvatar";

/**
 * The minimal user shape this picker needs.
 *
 * Structurally identical to Task's `PreparorUser`, deliberately: declaring it
 * here keeps this shared component from importing out of `pages/`, and TS's
 * structural typing means `PreparorUser[]` still passes without a cast.
 */
export type EmployeeOption = {
  id?: string;
  user_id?: string;
  name?: string;
  role?: string;
};

/**
 * Checkbox multi-select user picker — mirrors the payroll "Select Employees"
 * dropdown (search box + Select All + checkbox rows + Done). The trigger shows
 * the current count; clicking opens a centered popup portalled to <body> so a
 * transformed modal ancestor can't clip the fixed overlay (same reason
 * PreparorSelect uses a portal). Selection order is preserved: new picks are
 * appended, so callers that care about order (e.g. an approval chain) can rely
 * on the order of `values`.
 *
 * Shared by the Task assignee pickers (TaskCreate/TaskEdit) and the Assets
 * holder pickers (Add Asset / Manage holders) — it lived in `pages/Task/` until
 * Assets needed it too.
 */
export function EmployeeMultiSelect({
  values,
  onChange,
  options,
  disabled,
  placeholder = "Select people",
  emptyText = "No users available.",
  // Defaults keep the people-pickers reading exactly as before; Policy Rules
  // reuses this control for departments, roles and the like.
  searchPlaceholder = "Search employees...",
  countLabel = (n) => `${n} selected`,
  noMatchText = (q) => `No users match “${q}”.`,
}: {
  values: string[];
  onChange: (ids: string[]) => void;
  options: EmployeeOption[];
  disabled?: boolean;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  countLabel?: (count: number) => string;
  /** The whole no-match line, so callers outside people pickers stay grammatical. */
  noMatchText?: (query: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Third thing a Radix modal Dialog does to this portalled popup, after
  // `pointer-events: none` on <body> and the scroll-lock (both handled below):
  // it traps focus. The trap keeps `focusin`/`focusout` listeners on
  // `document` and, whenever focus lands outside the dialog's own subtree,
  // puts it straight back. This popup lives under <body>, so the search box
  // lost focus the moment it got it and every keystroke went to the dialog —
  // typing did nothing at all.
  //
  // These listeners are on `document` in the CAPTURE phase, so they see the
  // event before the trap's bubble-phase ones and can stop it reaching them.
  // Stopping `focusin` alone is not enough: the trap also acts on the
  // `focusout` of the element being left, which fires inside the dialog rather
  // than in here. Both are scoped to focus moves that actually involve this
  // popup, so the dialog's own fields keep their normal focus/blur behaviour.
  //
  // Focus is taken here rather than with `autoFocus` on the input so it lands
  // only once the guards are attached — otherwise the trap wins the first race.
  useEffect(() => {
    if (!open) return;
    const involvesPopup = (n: EventTarget | null) =>
      n instanceof Node && !!popupRef.current?.contains(n);
    const guard = (e: FocusEvent) => {
      if (involvesPopup(e.target) || involvesPopup(e.relatedTarget))
        e.stopPropagation();
    };
    document.addEventListener("focusin", guard, true);
    document.addEventListener("focusout", guard, true);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("focusin", guard, true);
      document.removeEventListener("focusout", guard, true);
    };
  }, [open]);

  const idOf = (u: EmployeeOption) => u.id || u.user_id || "";

  const filtered = options.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const name = (u.name || "").toLowerCase();
    const role = (u.role || "").toLowerCase();
    return name.includes(q) || role.includes(q);
  });

  const allSelected =
    options.length > 0 && options.every((u) => values.includes(idOf(u)));

  const toggle = (id: string) => {
    if (values.includes(id)) onChange(values.filter((v) => v !== id));
    else onChange([...values, id]);
  };

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange(options.map(idOf).filter(Boolean));
  };

  const countText =
    values.length === 0
      ? placeholder
      : options.length > 0 && values.length >= options.length
        ? `All (${options.length})`
        : countLabel(values.length);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative">
      {/* Trigger — shows the current count; opens the picker as a centered
          popup so the list never gets clipped near the modal edges. */}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setQuery("");
          setOpen(true);
        }}
        className={`flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <Users className="h-3.5 w-3.5 opacity-50 shrink-0 mr-2" />
        <span
          className={`flex-1 min-w-0 truncate ${
            values.length ? "" : "text-muted-foreground"
          }`}
        >
          {countText}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 opacity-60 ml-1 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 opacity-60 ml-1 shrink-0" />
        )}
      </button>

      {open &&
        createPortal(
          // `pointer-events-auto` is REQUIRED, not decorative. When this picker
          // is opened from inside a Radix modal (e.g. the Assets "Manage
          // holders" dialog), Radix's DismissableLayer sets
          // `document.body.style.pointerEvents = "none"` and re-enables it only
          // on the dialog's own layer. This popup portals to <body>, so it
          // inherits `none` and every click in it — Close, the checkboxes,
          // Select All, Done — silently does nothing. Opting back in here fixes
          // that; it's a no-op when there's no modal (TaskCreate/TaskEdit are
          // plain pages). See PreparorSelect for the same portal pattern.
          <div
            ref={popupRef}
            className="fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-auto"
            onMouseDown={close}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div
              className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Search header — full-width input, no lens icon (payroll style) */}
              <div className="flex items-center gap-2 px-2 py-2 border-b border-gray-100 bg-gray-50">
                <input
                  ref={inputRef}
                  type="text"
                  autoComplete="off"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                />
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    close();
                  }}
                  aria-label="Close"
                  className="opacity-60 hover:opacity-100 shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Select all */}
              <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleAll();
                  }}
                  className={`w-full py-1.5 rounded-md font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                    allSelected
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-200"
                  }`}
                >
                  {allSelected ? (
                    <>
                      <X className="w-3 h-3" /> Deselect All
                    </>
                  ) : (
                    <>
                      <Check className="w-3 h-3" /> Select All
                    </>
                  )}
                </button>
              </div>

              {/* ~5 rows visible at a time; the rest scrolls internally. The
                  wheel/touch guards are the same fix SearchableCombobox needs:
                  a modal Dialog's scroll-lock (react-remove-scroll) cancels
                  wheel events that reach `document`, and this list sits outside
                  the dialog subtree it whitelists — so without this the list
                  only moved by dragging its scrollbar. */}
              <ul
                className="max-h-[210px] overflow-y-auto py-1"
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-gray-400 italic">
                    {query ? noMatchText(query) : emptyText}
                  </li>
                ) : (
                  filtered.map((u) => {
                    const id = idOf(u);
                    const isSelected = values.includes(id);
                    return (
                      <li
                        key={id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          toggle(id);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                          isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                        />
                        <PersonAvatar
                          userId={u.user_id}
                          employeeId={u.id}
                          name={u.name}
                          size={24}
                        />
                        <span
                          className={`flex-1 min-w-0 truncate ${
                            isSelected ? "font-medium text-indigo-700" : ""
                          }`}
                        >
                          {u.name} {u.role ? `(${u.role})` : ""}
                        </span>
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                        )}
                      </li>
                    );
                  })
                )}
              </ul>

              {/* Footer */}
              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex justify-between items-center text-xs">
                <span className="text-gray-600">{countLabel(values.length)}</span>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    close();
                  }}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
